import { useEffect, useState, useContext, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAppStore } from 'Store/App'
import { useProjectStore } from 'Store/Project'
import { useMachineStore } from 'Store/Machine'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import { useInventoryStore } from '../../../../Store/Inventory'
import { PileExecutionAttachmentContext } from '../PileExecutionAttachment/Context'
import Swal from 'sweetalert2'
import { inferScanTypeFromCode, type ScanInventoryType } from 'Utils/scanInventoryType'
import { parseInventoryScanInput } from 'Utils/extractScannedCode'
import { getLocationFromHole, getPreboreFromHole, parsePreborePayload } from 'Utils/prebore'
import type { PileExecutionMode } from './Components/ModeSelectionModal'

interface HolePreboreFormState {
  preboreDepth: string | null
  preboreDrillSize: string | null
  pileNo: string | null
  gridLine: string | null
  location: string | null
}

interface PileScanFormState {
  projectMachineId: number | null
  pileNo: string | null
  gridLine: string | null
  location: string | null
}

const EMPTY_HOLE_PREBORE_FORM: HolePreboreFormState = {
  preboreDepth: null,
  preboreDrillSize: null,
  pileNo: null,
  gridLine: null,
  location: null,
}

const EMPTY_PILE_SCAN_FORM: PileScanFormState = {
  projectMachineId: null,
  pileNo: null,
  gridLine: null,
  location: null,
}

const ViewModel = () => {
  const navigate = useNavigate()
  const { setOpen, setCurrentPileHoleId } = useContext(PileExecutionAttachmentContext)
  const { id: projectId } = useParams<{ id: string }>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingPrebore, setIsSavingPrebore] = useState(false)
  const [isModeModalOpen, setIsModeModalOpen] = useState(false)
  const [activeExecutionMode, setActiveExecutionMode] = useState<PileExecutionMode>('drive')
  const [searchParams] = useSearchParams()
  const [selected, setSelected] = useState<'user' | 'environment' | undefined>('environment')
  const [startScan, setStartScan] = useState(false)
  const [loadingScan, setLoadingScan] = useState(false)
  const [manualScanInput, setManualScanInput] = useState('')
  const [scannedData, setScannedData] = useState<string | null>(null)
  const [scannedType, setScannedType] = useState<ScanInventoryType | null>(null)
  const [scannedItemId, setScannedItemId] = useState<string | null>(null)
  const [isUseCamera, setIsUseCamera] = useState(false)
  const [hasCamera, setHasCamera] = useState(false)
  const pileHoleId = searchParams.get('pileHoleId')
  const [openPreboreModal, setOpenPreboreModal] = useState(false)
  const [holePreboreFormState, setHolePreboreFormState] =
    useState<HolePreboreFormState>(EMPTY_HOLE_PREBORE_FORM)
  const [pileScanFormState, setPileScanFormState] =
    useState<PileScanFormState>(EMPTY_PILE_SCAN_FORM)

  const setShowBackButton = useAppStore(state => state.setShowBackButton, shallow)

  const { inventorySelected, getInventory, setSelectedInventory } = useInventoryStore(
    state => ({
      inventorySelected: state.selected,
      getInventory: state.getOne,
      setSelectedInventory: state.setSelected,
    }),
    shallow
  )
  const { machineData, getMachineData } = useMachineStore(
    state => ({
      machineData: state.data,
      getMachineData: state.getAll,
    }),
    shallow
  )

  const {
    projectData,
    getOne,
    pileHole,
    pileDriving,
    projectMachines,
    getMachines,
    getPileHoleInfo,
    createPileHole,
    executePileHolePrebore,
    updatePileHolePrebore,
    removePileDriving,
    closeHole,
    setPileHole,
    openProject,
    closeProject,
  } = useProjectStore(
    state => ({
      projectData: state.selected,
      getOne: state.getOne,
      pileHole: state.pileHole,
      pileDriving: state.pileDriving,
      projectMachines: state.machines,
      getPileHoleInfo: state.getPileHoleInfo,
      createPileHole: state.createPileHole,
      executePileHolePrebore: state.executePileHolePrebore,
      updatePileHolePrebore: state.updatePileHolePrebore,
      removePileDriving: state.removePileDriving,
      closeHole: state.closeHole,
      setPileHole: state.setPileHole,
      openProject: state.open,
      closeProject: state.close,
      getMachines: state.getAllProjectMachine,
    }),
    shallow
  )

  const holePrebore = useMemo(() => getPreboreFromHole(pileHole), [pileHole])
  const effectiveExecutionMode: PileExecutionMode =
    pileHole?.executionMode === 'prebore_only'
      ? 'prebore_only'
      : pileHole?.executionMode === 'drive'
      ? 'drive'
      : activeExecutionMode

  const projectMachineOptions = projectMachines.map(pm => {
    const machine = machineData.find(m => m.id === pm.machineId)
    return {
      label: `${machine?.brand ?? ''} ${machine?.machineModel ?? ''} (${machine?.machineCode}) - ${
        machine?.machineType?.title
      }`,
      value: pm.machineId,
    }
  })

  const showAddButtonAttachmentDrivedPile = (index: number) => {
    const inventory = pileHole?.itemInventory ?? []
    const item = inventory[index]

    if (item) {
      if (inventory.length === 1) {
        return false
      } else {
        if ((index - 1) % 2 === 0) {
          return true
        }
      }
    } else {
      return false
    }
  }

  const fetchProject = () => {
    getMachineData()
    getMachines(projectId ? projectId : '0')
    getOne(projectId ? parseInt(projectId) : 0)
  }

  const fetchPileHoleInfo = () => {
    if (pileHoleId && projectId) {
      getPileHoleInfo(parseInt(projectId), parseInt(pileHoleId))
    }
  }

  const onViewList = () => {
    navigate(`/project/${projectId}/pile-executed-list`)
  }

  const lookupInventory = async (rawValue: string) => {
    const parsed = parseInventoryScanInput(rawValue)
    if (!parsed.inventoryCode) return

    setSelectedInventory(null)
    setScannedData(parsed.inventoryCode)
    setScannedType(parsed.type)
    setScannedItemId(parsed.itemId ?? null)

    const res = await getInventory(parsed.inventoryCode, {
      type: parsed.type,
      itemId: parsed.itemId,
      soNo: parsed.soNo,
    })

    if (res.success) {
      if (res.data && parseInt(res.data.projectId) !== parseInt(projectId ?? '0')) {
        Swal.fire({
          icon: 'warning',
          title: 'รายการนี้ถูกใช้งานในโครงการอื่น',
          confirmButtonText: 'ปิด',
        })
        setSelectedInventory(null)
        setScannedData(null)
        setScannedType(null)
        setScannedItemId(null)
        return
      }
      return
    }

    toast.error('ไม่พบรายการเสาเข็ม')
    setScannedData(null)
    setScannedType(null)
    setScannedItemId(null)
    Swal.fire({
      icon: 'warning',
      title: 'ไม่พบรหัสเสาเข็มนี้อยู่ในโครงการนี้',
      confirmButtonText: 'ปิด',
    })
  }

  const handleScan = async (scanData: any) => {
    setLoadingScan(true)
    const rawValue = scanData[0].rawValue.trim()

    if (rawValue) {
      await lookupInventory(rawValue)
      setStartScan(false)
    }
    setLoadingScan(false)
  }

  const handleInputCodeChange = (e: any) => {
    setManualScanInput(e.target.value)
  }

  const handleManualScan = async () => {
    if (!manualScanInput) return
    await lookupInventory(manualScanInput)
  }

  const handleRemoveScanned = async (inventoryCode: string, itemId?: string) => {
    if (pileHole?.id && projectId) {
      await removePileDriving(parseInt(projectId), {
        inventoryCode,
        pileHoleId: pileHole?.id,
        type: inferScanTypeFromCode(inventoryCode),
        itemId,
      })
      await getPileHoleInfo(parseInt(projectId), pileHole.id)
    }
  }

  const handleCancelSelected = () => {
    setSelectedInventory(null)
    setManualScanInput('')
    setScannedData(null)
    setScannedType(null)
    setScannedItemId(null)
    setPileScanFormState(EMPTY_PILE_SCAN_FORM)
  }

  const savePreboreForHole = async (
    holeId: number,
    depth: string | null,
    drillSize: string | null,
    pileNo: string | null,
    gridLine: string | null,
    location: string | null
  ) => {
    if (!projectId || (!depth && !drillSize)) {
      return { success: true, data: null }
    }

    return updatePileHolePrebore(
      parseInt(projectId),
      holeId,
      parsePreborePayload(depth ?? '', drillSize ?? '', pileNo, gridLine, location)
    )
  }

  const handlePileDriving = async () => {
    if (!projectId || !scannedData) return
    setIsSubmitting(true)
    try {
      const { success, data } = await pileDriving(parseInt(projectId), {
        inventoryCode: scannedData,
        type: scannedType ?? inferScanTypeFromCode(scannedData),
        itemId: scannedItemId ?? inventorySelected?.pileCode ?? undefined,
        pileHoleId: pileHole?.id ?? null,
        projectMachineId: pileScanFormState.projectMachineId,
        pileNo: pileScanFormState.pileNo,
        gridLine: pileScanFormState.gridLine,
        location: pileScanFormState.location,
      })
      if (!success) {
        setIsSubmitting(false)
        toast.error(data)
        return
      }
      const holeId = data.projectPileHoleId ?? pileHole?.id
      if (
        holeId &&
        (holePreboreFormState.preboreDepth || holePreboreFormState.preboreDrillSize)
      ) {
        const preboreResult = await savePreboreForHole(
          holeId,
          holePreboreFormState.preboreDepth,
          holePreboreFormState.preboreDrillSize,
          holePreboreFormState.pileNo,
          holePreboreFormState.gridLine,
          holePreboreFormState.location
        )
        if (!preboreResult.success) {
          setIsSubmitting(false)
          toast.error(preboreResult.data)
          return
        }
      }
      await getPileHoleInfo(parseInt(projectId), data.projectPileHoleId)
      setSelectedInventory(null)
      setPileScanFormState(EMPTY_PILE_SCAN_FORM)
      setManualScanInput('')
      setScannedData(null)
      setScannedType(null)
      setScannedItemId(null)
      toast.success('Pile Driving Success')
      setIsSubmitting(false)
    } catch (err: any) {
      setIsSubmitting(false)
      toast.error(err.message)
    }
  }

  const openModeSelectionModal = () => {
    if (pileHole?.status === 'completed') return
    setIsModeModalOpen(true)
  }

  const closeModeSelectionModal = () => {
    if (isSavingPrebore) return
    setIsModeModalOpen(false)
  }

  const handleSelectExecutionMode = (mode: PileExecutionMode) => {
    if (!projectId) return
    setSelectedInventory(null)
    setScannedData(null)
    setScannedType(null)
    setPileScanFormState(EMPTY_PILE_SCAN_FORM)

    if (mode === 'drive') {
      if (pileHole?.executionMode === 'prebore_only') {
        setPileHole({
          ...pileHole,
          executionMode: 'drive',
        })
      }
      // Keep hole prebore fields when switching to drive (re-hydrate from pileHole effect)
      if (!pileHole) {
        setHolePreboreFormState(EMPTY_HOLE_PREBORE_FORM)
      }
      setActiveExecutionMode('drive')
      setIsModeModalOpen(false)
      setStartScan(true)
      return
    }

    // Fresh prebore-only flow: reset hole form unless an existing hole will re-hydrate it
    if (!pileHole) {
      setHolePreboreFormState(EMPTY_HOLE_PREBORE_FORM)
    }
    setActiveExecutionMode('prebore_only')
    setIsModeModalOpen(false)
    setStartScan(false)
  }

  const handleSavePreboreOnly = async () => {
    if (!projectId) return
    if (!holePreboreFormState.preboreDepth && !holePreboreFormState.preboreDrillSize) {
      toast.error('กรุณาระบุความลึกหรือขนาดสว่าน')
      return
    }

    setIsSavingPrebore(true)
    try {
      let holeId = pileHole?.id
      if (!holeId) {
        const createResult = await createPileHole(parseInt(projectId), {
          executionMode: 'prebore_only',
        })
        if (!createResult.success) {
          toast.error(createResult.data ?? 'ไม่สามารถสร้างหลุมได้')
          return
        }
        holeId = createResult.data?.id
      }

      if (!holeId) {
        toast.error('ไม่สามารถสร้างหลุมได้')
        return
      }

      const { success, data } = await executePileHolePrebore(parseInt(projectId), holeId, {
        ...parsePreborePayload(
          holePreboreFormState.preboreDepth ?? '',
          holePreboreFormState.preboreDrillSize ?? '',
          holePreboreFormState.pileNo,
          holePreboreFormState.gridLine,
          holePreboreFormState.location
        ),
      })
      if (!success) {
        toast.error(data)
        return
      }

      toast.success('บันทึก prebore สำเร็จ')
      await getPileHoleInfo(parseInt(projectId), holeId)
    } finally {
      setIsSavingPrebore(false)
    }
  }

  const handlePileClose = async () => {
    if (!projectId) return
    try {
      await closeHole(parseInt(projectId), {
        pileHoleId: pileHole?.id ?? null,
      })
      setSelectedInventory(null)
      setManualScanInput('')
      setScannedData(null)
      setScannedType(null)
      setScannedItemId(null)
      setPileHole(null)
      toast.success('Pile Close Success')
    } catch (err: any) {
      toast.error(err.message)
    }
  }

  const handleExecutedPileAttachment = (index: number) => {
    const item = pileHole?.itemInventory[index]
    const previousItem = pileHole?.itemInventory[index - 1]

    if (!item || !previousItem) return

    setOpen(true)
    setCurrentPileHoleId({
      pile1HoleId: item?.id ?? '',
      pile2HoleId: previousItem?.id ?? '',
    })
  }

  const toggleUseCamera = () => {
    const nextValue = !isUseCamera
    if (!hasCamera) {
      toast.error('No camera found')
      return
    }
    setIsUseCamera(nextValue)
  }

  const onOpenproject = () => {
    if (projectId) {
      Swal.fire({
        title: 'ยืนยันการเปิดโครงการ',
        text: 'คุณต้องการเปิดโครงการนี้ใช่หรือไม่?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      }).then(result => {
        if (result.isConfirmed) {
          openProject(parseInt(projectId)).then(({ success, data }) => {
            if (!success) {
              toast.error(data)
            } else {
              toast.success('เปิดโครงการสำเร็จ')
              getOne(parseInt(projectId))
            }
          })
        }
      })
    }
  }

  const onCloseproject = () => {
    if (projectId) {
      Swal.fire({
        title: 'ยืนยันการปิดโครงการ',
        text: 'คุณต้องการปิดโครงการนี้ใช่หรือไม่?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      }).then(result => {
        if (result.isConfirmed) {
          closeProject(parseInt(projectId)).then(({ success, data }) => {
            if (!success) {
              toast.error(data)
            } else {
              toast.success('ปิดโครงการสำเร็จ')
              getOne(parseInt(projectId))
            }
          })
        }
      })
    }
  }

  const onChangeHolePreboreFormState = (field: keyof HolePreboreFormState, value: any) => {
    setHolePreboreFormState(prevState => ({
      ...prevState,
      [field]: value,
    }))
  }

  const onChangePileScanFormState = (field: keyof PileScanFormState, value: any) => {
    setPileScanFormState(prevState => ({
      ...prevState,
      [field]: value,
    }))
  }

  const handleSavePrebore = async (
    depth: string,
    drillSize: string,
    pileNo: string,
    gridLine: string,
    location: string
  ) => {
    if (!projectId || !pileHole?.id) return
    setIsSavingPrebore(true)
    const { success, data } = await updatePileHolePrebore(
      parseInt(projectId),
      pileHole.id,
      parsePreborePayload(depth, drillSize, pileNo, gridLine, location)
    )
    setIsSavingPrebore(false)

    if (!success) {
      toast.error(data)
      return
    }

    setHolePreboreFormState({
      preboreDepth: depth || null,
      preboreDrillSize: drillSize || null,
      pileNo: pileNo || null,
      gridLine: gridLine || null,
      location: location || null,
    })
    toast.success('บันทึกตั้งค่า prebore สำเร็จ')
    setOpenPreboreModal(false)
  }

  useEffect(() => {
    if (pileHole) {
      const prebore = getPreboreFromHole(pileHole)
      const locationFields = getLocationFromHole(pileHole)
      setHolePreboreFormState({
        preboreDepth:
          prebore.preboreDepth != null ? String(prebore.preboreDepth) : null,
        preboreDrillSize:
          prebore.preboreDrillSize != null ? String(prebore.preboreDrillSize) : null,
        pileNo: locationFields.pileNo,
        gridLine: locationFields.gridLine,
        location: locationFields.location,
      })
    }
  }, [pileHole])

  useEffect(() => {
    if (inventorySelected) {
      setPileScanFormState(EMPTY_PILE_SCAN_FORM)
    }
    // Reset scan form when a new inventory is selected (fresh empty inputs per scan)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventorySelected?.id])

  useEffect(() => {
    fetchProject()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  useEffect(() => {
    fetchPileHoleInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, pileHoleId])

  useEffect(() => {
    async function checkCameraAvailability () {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
          alert('MediaDevices API is not supported in this browser')
          console.warn('MediaDevices API is not supported in this browser')
          setHasCamera(false)
          return
        }

        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        stream.getTracks().forEach(track => track.stop())

        const devices = await navigator.mediaDevices.enumerateDevices()
        setHasCamera(devices.some(device => device.kind === 'videoinput'))
      } catch (error) {
        console.error('Error checking camera availability:', error)
        setHasCamera(false)
      }
    }

    checkCameraAvailability()
  }, [])

  useEffect(() => {
    setShowBackButton(true)

    return () => {
      setShowBackButton(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isSubmitting,
    isSavingPrebore,
    projectMachineOptions,
    projectState: projectData?.projectState,
    inventorySelected,
    selected,
    pileHole,
    projectId,
    setSelected,
    startScan,
    setStartScan,
    isModeModalOpen,
    effectiveExecutionMode,
    loadingScan,
    setLoadingScan,
    hasCamera,
    manualScanInput,
    scannedData,
    setScannedData,
    showAddButtonAttachmentDrivedPile,
    handlePileDriving,
    openModeSelectionModal,
    closeModeSelectionModal,
    handleSelectExecutionMode,
    handleSavePreboreOnly,
    handleScan,
    handleManualScan,
    handleInputCodeChange,
    handleCancelSelected,
    handleRemoveScanned,
    handlePileClose,
    handleExecutedPileAttachment,
    onViewList,
    isUseCamera,
    toggleUseCamera,
    onOpenproject,
    onCloseproject,
    holePreboreFormState,
    onChangeHolePreboreFormState,
    pileScanFormState,
    onChangePileScanFormState,
    holePrebore,
    openPreboreModal,
    setOpenPreboreModal,
    handleSavePrebore,
  }
}

export default ViewModel
